/**
 * Vanturis Passport Phase 8 — product route table.
 *
 * ONE route definition serves both:
 *   • path-mode:  mounted at "/passport" on the main Vanturis host (today)
 *   • host-mode:  mounted at "" when served on a configured Passport
 *     hostname (future passport.vanturis.com — no DNS required to test)
 *
 * Backward compatibility: legacy /passport/owner/:assetId redirects to
 * the new asset route AFTER the shell's auth handling — the redirect
 * itself carries no asset data, so no existence leak occurs.
 */
import { lazy } from "react";
import { Route, Navigate, useParams } from "react-router-dom";
import { PassportProtected } from "@/passport/PassportShell";
import { pp } from "@/passport/product";

// P1B: Passport product pages are lazy-loaded so the marketplace homepage
// bundle no longer carries the full Passport application. Route paths, auth
// guards (PassportProtected) and the legacy redirect are unchanged — only
// load timing differs. Suspense is handled by PassportShell (protected pages)
// and the app-level boundary (public passport pages).
const PassportLanding = lazy(() => import("@/passport/pages/PassportLanding"));
const PassportDashboard = lazy(() => import("@/passport/pages/PassportDashboard"));
const PassportAsset = lazy(() => import("@/passport/pages/PassportAsset"));
const PassportInspectionDetail = lazy(() => import("@/passport/pages/PassportInspectionDetail"));
const PassportClaims = lazy(() => import("@/passport/pages/PassportClaims"));
const PassportClaimNew = lazy(() => import("@/passport/pages/PassportClaimNew"));
const PassportProvider = lazy(() => import("@/passport/pages/PassportProvider"));
const PassportLogin = lazy(() => import("@/passport/pages/PassportAuth").then((m) => ({ default: m.PassportLogin })));
const PassportSignup = lazy(() => import("@/passport/pages/PassportAuth").then((m) => ({ default: m.PassportSignup })));
const PassportSharedList = lazy(() => import("@/passport/pages/PassportShared").then((m) => ({ default: m.PassportSharedList })));
const PassportSharedAsset = lazy(() => import("@/passport/pages/PassportShared").then((m) => ({ default: m.PassportSharedAsset })));
const PassportRedeem = lazy(() => import("@/passport/pages/PassportShared").then((m) => ({ default: m.PassportRedeem })));

function LegacyOwnerRedirect() {
  const { assetId } = useParams();
  return <Navigate to={pp(`/assets/${assetId}`)} replace />;
}

export function passportRoutes(base = "/passport") {
  const b = base;
  const root = b === "" ? "/" : b;
  return (
    <>
      {/* Public marketing */}
      <Route path={root} element={<PassportLanding />} />
      <Route path={`${b}/about`} element={<Navigate to={root} replace />} />

      {/* Product entry — SAME Vanturis account (no separate Passport auth) */}
      <Route path={`${b}/login`} element={<PassportLogin />} />
      <Route path={`${b}/signup`} element={<PassportSignup />} />

      {/* Authenticated product surface */}
      <Route path={`${b}/dashboard`} element={<PassportProtected><PassportDashboard /></PassportProtected>} />
      <Route path={`${b}/assets/:assetId`} element={<PassportProtected><PassportAsset /></PassportProtected>} />
      <Route path={`${b}/assets/:assetId/inspections/:eventId`} element={<PassportProtected><PassportInspectionDetail /></PassportProtected>} />
      <Route path={`${b}/shared`} element={<PassportProtected><PassportSharedList /></PassportProtected>} />
      <Route path={`${b}/shared/redeem`} element={<PassportProtected><PassportRedeem /></PassportProtected>} />
      <Route path={`${b}/shared/:assetId`} element={<PassportProtected><PassportSharedAsset /></PassportProtected>} />
      <Route path={`${b}/claims`} element={<PassportProtected><PassportClaims /></PassportProtected>} />
      <Route path={`${b}/claims/new`} element={<PassportProtected><PassportClaimNew /></PassportProtected>} />
      {/* Phase 9 — Trusted Provider workspace (flag-gated backend → coming-soon state) */}
      <Route path={`${b}/provider`} element={<PassportProtected><PassportProvider /></PassportProtected>} />

      {/* Backward-compatible legacy route */}
      <Route path={`${b}/owner/:assetId`} element={<LegacyOwnerRedirect />} />
    </>
  );
}
